// Frontend environment configuration (this file). Optional overrides go in .env.local, not .env.
// See environment.example in the frontend root for variable names.

function buildSentryConfig(isDevelopment) {
  const sentryDsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();
  const envOverride = (process.env.REACT_APP_SENTRY_ENVIRONMENT || "").trim();
  const sentryEnvironment = envOverride || (isDevelopment ? "development" : "production");
  const rawRate = process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE;
  let sentryTracesSampleRate =
    rawRate === undefined || rawRate === "" ? NaN : Number.parseFloat(rawRate, 10);
  if (!Number.isFinite(sentryTracesSampleRate)) {
    sentryTracesSampleRate = isDevelopment ? 0 : 0.1;
  }
  sentryTracesSampleRate = Math.min(1, Math.max(0, sentryTracesSampleRate));
  return { sentryDsn, sentryEnvironment, sentryTracesSampleRate };
}

const getEnvironmentConfig = () => {
  const hostname = window.location.hostname;
  
  // Local development (localhost/127.0.0.1) - use local backend
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    let backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    // If env var is set, check if it's a local URL (localhost, 127.0.0.1, or IP address)
    // If it's not local, ignore it and use the default local backend
    if (backendUrl) {
      const isLocalUrl = backendUrl.includes('localhost') || 
                        backendUrl.includes('127.0.0.1') || 
                        /^\d+\.\d+\.\d+\.\d+/.test(backendUrl) ||
                        /^http:\/\/\d+\.\d+\.\d+\.\d+/.test(backendUrl);
      
      if (!isLocalUrl) {
        // Ignore non-local URLs when on localhost - use local backend instead
        backendUrl = null;
      }
    }
    
    if (!backendUrl) {
      // Default: same-machine backend. For mobile/device testing on same WiFi, set REACT_APP_BACKEND_URL=http://<your-lan-ip>:8000 in .env (e.g. ipconfig getifaddr en0 on macOS).
      backendUrl = 'http://localhost:8000';
    }
    
    // Ensure protocol is present (use http for local, https for remote)
    if (!backendUrl.startsWith('http://') && !backendUrl.startsWith('https://')) {
      // If it's a local IP or localhost, use http, otherwise https
      if (backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1') || /^\d+\.\d+\.\d+\.\d+/.test(backendUrl)) {
        backendUrl = `http://${backendUrl}`;
      } else {
        backendUrl = `https://${backendUrl}`;
      }
    }
    
    return {
      backendUrl,
      isDevelopment: true,
      isMobile: false,
      ...buildSentryConfig(true),
    };
  }
  
  // Development environments (dev domain, Vercel previews)
  // - dev.runcards.co (dev domain)
  // - Vercel preview/deployments (contains 'dev', 'preview', or 'vercel.app' but not 'runcards.co' or 'run.cards')
  const isDevHostname = hostname === 'dev.runcards.co' ||
                        (hostname.includes('dev') && !hostname.includes('runcards.co') && !hostname.includes('run.cards')) ||
                        (hostname.includes('preview') && !hostname.includes('runcards.co') && !hostname.includes('run.cards')) ||
                        (hostname.includes('vercel.app') && !hostname.includes('runcards.co') && !hostname.includes('run.cards'));
  
  if (isDevHostname) {
    let backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://runcards-dev.up.railway.app';
    // Ensure protocol is present
    if (!backendUrl.startsWith('http://') && !backendUrl.startsWith('https://')) {
      backendUrl = `https://${backendUrl}`;
    }
    return {
      backendUrl,
      isDevelopment: true,
      isMobile: false,
      ...buildSentryConfig(true),
    };
  }
  
  // Mobile testing environments
  const mobileIPs = [
    process.env.REACT_APP_MOBILE_IP || 'localhost'
  ];
  
  if (mobileIPs.includes(hostname)) {
    return {
      backendUrl: `http://${hostname}:8000`,
      isDevelopment: true,
      isMobile: true,
      ...buildSentryConfig(true),
    };
  }
  
  // Production environment
  if (process.env.REACT_APP_BACKEND_URL) {
    let url = process.env.REACT_APP_BACKEND_URL;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }
    return {
      backendUrl: url,
      isDevelopment: false,
      isMobile: false,
      ...buildSentryConfig(false),
    };
  }
  
  // Default production
  return {
    backendUrl: 'https://runcards-production.up.railway.app',
    isDevelopment: false,
    isMobile: false,
    ...buildSentryConfig(false),
  };
};

export const environment = getEnvironmentConfig();
export const {
  backendUrl,
  isDevelopment,
  isMobile,
  sentryDsn,
  sentryEnvironment,
  sentryTracesSampleRate,
} = environment;

// Debug logging in development
if (process.env.NODE_ENV === 'development') {
  console.log('Environment config:', {
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
    backendUrl,
    isDevelopment,
    envVar: process.env.REACT_APP_BACKEND_URL
  });
}
